import React, { useState } from 'react'
import { Button } from 'ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'ui/command'
import { Popover, PopoverContent, PopoverTrigger } from 'ui/popover'
import { LiaTimesSolid } from 'react-icons/lia'
import { IoMdTime } from 'react-icons/io'
import { Label } from 'ui/label'
import CustomTimePicker from 'components/datetime/CustomTimePicker'

export interface DaysAndTimesPickerProps {
    options: { id: string; name: string }[]
    value: string[]
    onChange: (newValue: string[]) => void
    allTimes: { [key: string]: string[] }
    onTimesChange: (newTimes: { [key: string]: string[] }) => void
    /** When set, day removal updates both days and times in one call (avoids form state getting out of sync). */
    onDaysAndTimesChange?: (value: { days: string[]; times: Record<string, string[]> }) => void
    placeholder?: string
}

interface TagProps {
    label: string
    onDelete: () => void
}

const Tag: React.FC<TagProps> = ({ label, onDelete }) => (
    <div className='flex items-center bg-card border rounded-full px-2 py-1'>
        <span>{label}</span>
        <LiaTimesSolid onClick={onDelete} className='ml-2 cursor-pointer w-4 h-4' />
    </div>
)

const DaysAndTimesPicker: React.FC<DaysAndTimesPickerProps> = ({
    options,
    value,
    onChange,
    allTimes,
    onTimesChange,
    onDaysAndTimesChange,
    placeholder = 'Select days of the week'
}) => {
    const [open, setOpen] = useState(false)
    const [inputValue, setInputValue] = useState('')
    const [customTimeDay, setCustomTimeDay] = useState<string | null>(null)
    const [newCustomTime, setNewCustomTime] = useState<{ [key: string]: string }>({})

    const handleAddOption = (option: string) => {
        if (!value.includes(option)) {
            onChange([...value, option])
        }
        setInputValue('')
        setOpen(false)
    }

    const handleRemoveDay = (dayId: string) => {
        const updatedDays = value.filter(day => day !== dayId)
        const updatedTimes = { ...allTimes }
        delete updatedTimes[dayId]

        if (onDaysAndTimesChange) {
            onDaysAndTimesChange({ days: updatedDays, times: updatedTimes })
        } else {
            onChange(updatedDays)
            onTimesChange(updatedTimes)
        }
    }

    const handleRemoveTime = (day: string, time: string) => {
        const updatedDesignatedTimes = { ...allTimes }
        if (updatedDesignatedTimes[day]) {
            updatedDesignatedTimes[day] = updatedDesignatedTimes[day].filter(t => t !== time)
            if (updatedDesignatedTimes[day].length === 0) {
                handleRemoveDay(day)
            } else {
                onTimesChange(updatedDesignatedTimes)
            }
        }
    }

    const handleAddTime = (day: string, time: string) => {
        if (!time) return
        const updatedDesignatedTimes = { ...allTimes }
        if (!updatedDesignatedTimes[day]) {
            updatedDesignatedTimes[day] = []
        }
        if (!updatedDesignatedTimes[day].includes(time)) {
            updatedDesignatedTimes[day] = [...(updatedDesignatedTimes[day] || []), time]
        }
        onTimesChange(updatedDesignatedTimes)
        setNewCustomTime(prev => ({ ...prev, [day]: '' }))
    }

    const sortedDays = [...value].sort(
        (a, b) => options.findIndex(option => option.id === a) - options.findIndex(option => option.id === b)
    )

    return (
        <div className='w-full'>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button type='button' variant='outline' className='w-full justify-between !h-11'>
                        {value.length > 0 ? `${value.length} day(s) selected` : placeholder}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className='w-full p-0'>
                    <Command>
                        <CommandInput value={inputValue} onValueChange={setInputValue} placeholder='Search day...' />
                        <CommandList>
                            <CommandEmpty>No days found.</CommandEmpty>
                            <CommandGroup>
                                {options
                                    .filter(option => option.name.toLowerCase().includes(inputValue.toLowerCase()))
                                    .map(option => (
                                        <CommandItem
                                            key={option.id}
                                            value={option.id}
                                            onSelect={() => handleAddOption(option.id)}
                                        >
                                            {option.name}
                                        </CommandItem>
                                    ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            <div className='flex flex-wrap mt-2 gap-2'>
                {value.map((day, index) => {
                    const dayName = options.find(opt => opt.id === day)?.name || day
                    return <Tag key={index} label={dayName} onDelete={() => handleRemoveDay(day)} />
                })}
            </div>
            {sortedDays.map((day, index) => {
                const dayDesignatedTimes = allTimes[day] || []
                return (
                    <div key={index} className='mt-4'>
                        <div className='flex flex-col gap-y-2'>
                            <Label htmlFor={`designated_times_${day}`}>{`Custom time for ${day}`}</Label>
                            <div className='relative min-h-10 h-fit px-3 text-sm rounded-md gap-2 flex items-center w-full border bg-card'>
                                <div className='flex flex-wrap w-full h-full gap-2'>
                                    {dayDesignatedTimes.map((time, idx) => (
                                        <span key={idx} className='border rounded-full px-2 py-1 flex items-center'>
                                            <span>{time}</span>
                                            <LiaTimesSolid
                                                onClick={() => handleRemoveTime(day, time)}
                                                className='ml-2 cursor-pointer'
                                            />
                                        </span>
                                    ))}
                                </div>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            onClick={() => setCustomTimeDay(day)}
                                            type='button'
                                            className='absolute right-0 rounded-r-md inset-y-0 items-center p-0 rounded-l-none w-12 border ml-auto h-full'
                                            variant='outline'
                                        >
                                            <IoMdTime className='h-6 w-6 flex' />
                                        </Button>
                                    </PopoverTrigger>
                                    {customTimeDay === day && (
                                        <PopoverContent className='w-80 flex flex-col'>
                                            <div className='space-y-2'>
                                                <p className='font-medium leading-none'>Add Custom Time</p>
                                                <CustomTimePicker
                                                    name={`${day}_designated_time`}
                                                    value={newCustomTime[day] || ''}
                                                    onChange={(value: string) => {
                                                        setNewCustomTime(prev => ({
                                                            ...prev,
                                                            [day]: value
                                                        }))
                                                    }}
                                                />
                                            </div>
                                            <Button
                                                type='button'
                                                onClick={() => {
                                                    handleAddTime(day, newCustomTime[day])
                                                }}
                                                className='h-10 flex text-sm mt-2 ml-auto'
                                            >
                                                Add
                                            </Button>
                                        </PopoverContent>
                                    )}
                                </Popover>
                            </div>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

export default DaysAndTimesPicker
